import { DashboardDuck } from './DashboardDuck.typed'
import { debounce, call, put, select } from 'redux-saga/effects'
import {
  updateTitleSaga,
  cloneSaga,
  createShareKeySaga,
  sendReportInvitationLinkSaga,
  updateSchemaSaga,
  updateSourceSaga,
  updateOrderSaga
} from './sagas'
import { DASHBOARD_ACTIONS } from './DashboardDuck.actions'
import { StorageKeys, storeKeyedValue } from '@aytm/app/insights/dashboard/lib/storage'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { safelyExecuteSaga } from '@aytm/app/insights/dashboard/lib/safety'

// sagas
DashboardDuck.saga(function* updateTitle(duckFace) {
  yield debounce(
    500,
    [duckFace.action[DASHBOARD_ACTIONS.UPDATE_TITLE].actionType],
    function* onUpdateTitle(
      /**
       * @type {PayloadAction<{ title: string }>}
       */
      action
    ) {
      const [error, newTitle] = yield call(safelyExecuteSaga, updateTitleSaga, action.payload.title)
      if (error) {
        // TODO: record last operation's error in state
        void 0
      }

      yield error || !newTitle
        ? put(duckFace.action[DASHBOARD_ACTIONS.RECEIVE_TITLE]({ title: action.payload.oldTitle }))
        : put(duckFace.action[DASHBOARD_ACTIONS.RECEIVE_TITLE]({ title: newTitle }))
    }
  )
})

DashboardDuck.saga(function* clone(duckFace) {
  yield debounce(
    500,
    [duckFace.action[DASHBOARD_ACTIONS.CLONE_PORTAL].actionType],
    function* onClone(
      /**
       * @type {PayloadAction<{ title: string }>}
       */
      action
    ) {
      const {
        payload: { title }
      } = action
      const [cloneError, newPortal] = yield call(safelyExecuteSaga, cloneSaga, title)
      if (cloneError || !newPortal) {
        void 0
        // TODO: handle error
        return
      }
      yield put(duckFace.action[DASHBOARD_ACTIONS.CLONE_SET](newPortal))
    }
  )
})

DashboardDuck.saga(function* sendSchema(duckFace) {
  yield debounce(500, [duckFace.action[DASHBOARD_ACTIONS.SEND_SCHEMA].actionType], function* onSendSchema(action) {
    const { object, surveys: _surveys } = action.payload
    const surveys = yield select(state => state.currentData?.portalSurveys || [])

    const [error, response] = yield call(safelyExecuteSaga, updateSchemaSaga, object, surveys)

    if (error) {
      yield put(duckFace.action[DASHBOARD_ACTIONS.RESET_SCHEMA]({ error, data: null }))
    } else {
      const data = response?.data
      yield put(duckFace.action[DASHBOARD_ACTIONS.RESET_SCHEMA]({ data }))
    }
  })
})

DashboardDuck.saga(function* sendSource(duckFace) {
  yield debounce(500, [duckFace.action[DASHBOARD_ACTIONS.SEND_SOURCE].actionType], function* onSendSource(action) {
    const { object, surveys: _surveys } = action.payload
    const surveys = yield select(state => state.currentData?.portalSurveys || [])
    const [error, response] = yield call(safelyExecuteSaga, updateSourceSaga, object, surveys)
    if (error) {
      yield put(duckFace.action[DASHBOARD_ACTIONS.CHANGE_SCHEMA]({ error, data: null }))
    } else {
      const data = response?.data

      yield put(duckFace.action[DASHBOARD_ACTIONS.CHANGE_SCHEMA]({ data }))
    }
  })
})

//updateOrderSaga - DashboardDuck.action[DASHBOARD_ACTIONS.SEND_REPORTS_ORDER]({ object })
DashboardDuck.saga(function* sendReportsOrder(duckFace) {
  yield debounce(
    500,
    [duckFace.action[DASHBOARD_ACTIONS.SEND_REPORTS_ORDER].actionType],
    function* onSendReportsOrder(action) {
      const { schema } = action.payload
      const [error, response] = yield call(safelyExecuteSaga, updateOrderSaga, schema)
      if (error) {
        yield put(duckFace.action[DASHBOARD_ACTIONS.CHANGE_SCHEMA]({ error, data: null }))
      } else {
        const data = response?.data
        yield put(duckFace.action[DASHBOARD_ACTIONS.CHANGE_SCHEMA]({ data }))
      }
    }
  )
})

DashboardDuck.saga(function* updateLocalStorage(duckFace) {
  yield debounce(
    500,
    [
      duckFace.action[DASHBOARD_ACTIONS.CHANGE_SCHEMA].actionType,
      duckFace.action[DASHBOARD_ACTIONS.RESET_SCHEMA].actionType
      // logged(ExportDuck.action(EXPORT_ACTIONS.EXPORT_UPDATE_DIALOG))
    ],
    function* onUpdateSchemaForLS(_action) {
      const storageSchema = yield select(DashboardDuck.select.currentSchema)
      storeKeyedValue(
        staticStore.isSharedView ? StorageKeys.DASHBOARD_SCHEMA_SHARED : StorageKeys.DASHBOARD_SCHEMA,
        staticStore.dashboardID,
        storageSchema
      )
    }
  )
})

DashboardDuck.saga(function* createSharedKey(duckFace) {
  yield debounce(
    500,
    [duckFace.action[DASHBOARD_ACTIONS.REQUEST_SHARE_URL].actionType],
    function* onCreateSharedKey(action) {
      const { payload: uid } = action
      yield put(
        duckFace.action[DASHBOARD_ACTIONS.UPDATE_BACKEND_RESULT]({
          error: null,
          initialActionType: DASHBOARD_ACTIONS.REQUEST_SHARE_URL
        })
      )

      const [shareError, result] = yield call(safelyExecuteSaga, createShareKeySaga, { uid })
      const { status, error, shared_key } = result || {}
      // eslint-disable-next-line unicorn/prefer-ternary
      if (shareError || error || !status) {
        const errorMessage =
          'access.denied' === error?.code ? 'Creating invitation key denied' : 'Error creating shared key!'
        yield put(
          duckFace.action[DASHBOARD_ACTIONS.UPDATE_BACKEND_RESULT]({
            error: errorMessage,
            initialActionType: DASHBOARD_ACTIONS.REQUEST_SHARE_URL
          })
        )
      } else {
        yield put(duckFace.action[DASHBOARD_ACTIONS.STORE_SHARE_KEY](shared_key))
      }
    }
  )
})

DashboardDuck.saga(function* sendInvitation(duckFace) {
  yield debounce(
    500,
    [duckFace.action[DASHBOARD_ACTIONS.SEND_INVITATION].actionType],
    function* onSendInvitation(action) {
      const { sharedKey, email } = action.payload
      if (!sharedKey || !email) {
        return
      }
      const [invitationError, result] = yield call(safelyExecuteSaga, sendReportInvitationLinkSaga, {
        emails: Array.isArray(email) ? email.join(',') : email,
        shared_key: sharedKey
      })
      const { status, error } = result || {}
      if (invitationError || error || !status) {
        const errorMessage =
          staticStore.canUpdateDashboard || 'access.denied' === error?.code
            ? 'Sending invitation denied'
            : 'Error sending invitation!'
        yield put(duckFace.action[DASHBOARD_ACTIONS.SEND_INVITATION]({ sendStatus: 3, error: errorMessage }))
      } else {
        yield put(duckFace.action[DASHBOARD_ACTIONS.SEND_INVITATION]({ sendStatus: 2 }))
      }
    }
  )
})

export default DashboardDuck
